import React, { useState } from 'react';
import PropTypes from 'prop-types';
import AppTable from 'components/AppTable';
import ModalBusyContainer from 'components/ModalBusyContainer';
import withTableProps from 'HOC/types/withTable';
import { Button, Icon } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useAsyncFn } from 'react-use';
import { actions as modalActions } from 'redux/modal';
import { SendFileForm } from 'components/CommonForms';
import { transformTableData, getConfirmationModalConfig } from 'utils';
import { handleCertificateDownload } from 'utils/certificateDownloadHandler';
import ConfirmExternalEvidenceForm from './ConfirmExternalEvidenceForm';
import { getEvidenceHistoryConfig } from './helpers/getEvidenceHistoryTableConfig';
import { formatTrainingModuleLabel, formatTrainingPartLabel } from './helpers/formatTrainingLabels';

const useStyles = makeStyles(theme => ({
  btn: {
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:first-child': {
      marginRight: 20,
    },
    '&:last-child': {
      marginLeft: 20,
    },
  },
  btnDelete: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.contrastText,
  },
  btnWrapper: {
    marginTop: 20,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: {
    textAlign: 'center',
    '& .MuiDialog-paper': {
      width: '500px',
    },
  },
  wrapper: {
    textAlign: 'center',
  },
}));

const isExternalSource = source => source === 'external';
const mapEvidenceSourceLabel = source => {
  if (source === 'session') {
    return 'eTrat';
  }
  if (source === 'external') {
    return 'External';
  }
  return source;
};

const EvidenceHistoryTable = ({ tableData, helpers, userId }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const classes = useStyles();
  const [downloadingCertificateId, setDownloadingCertificateId] = useState(null);
  const handleCloseModal = () => dispatch(modalActions.closeModal());

  const [updateState, updateEvidence] = useAsyncFn(async (evidenceId, payload) => {
    const response = await helpers.updateExternalEvidence(userId, evidenceId, payload);
    if (!response?.error && !(response?.statusCode && response.statusCode >= 400)) {
      handleCloseModal();
      tableData.handleForceUpdate();
    }
    return response;
  });

  const [deleteState, deleteEvidence] = useAsyncFn(async evidenceId => {
    const response = await helpers.deleteExternalEvidence(userId, evidenceId);
    if (!response?.error && !(response?.statusCode && response.statusCode >= 400)) {
      handleCloseModal();
      tableData.handleForceUpdate();
    }
    return response;
  });

  const [addFileState, addFile] = useAsyncFn(async (evidenceId, formData) => {
    const data = await helpers.uploadEvidenceCertificate(userId, evidenceId, formData);
    if (!data?.error && !(data?.statusCode && data.statusCode >= 400)) {
      handleCloseModal();
      tableData.handleForceUpdate();
    }
    return data;
  });

  const [deleteFileState, deleteFile] = useAsyncFn(async evidenceId => {
    const data = await helpers.deleteEvidenceCertificate(userId, evidenceId);
    if (!data?.error && !(data?.statusCode && data.statusCode >= 400)) {
      handleCloseModal();
      tableData.handleForceUpdate();
    }
    return data;
  });

  const [, downloadFile] = useAsyncFn(async evidenceId => helpers.downloadEvidenceCertificate(userId, evidenceId));

  const handleEdit = event => {
    const evidenceId = Number(event.currentTarget.dataset.key);
    const row = (Array.isArray(tableData.data) ? tableData.data : []).find(item => Number(item.id) === evidenceId);
    if (!row || !isExternalSource(row.source)) {
      return;
    }

    dispatch(
      modalActions.openModal(
        {
          size: 'medium',
          title: t('trainingSessionDetails.editExternalEvidenceTitle'),
        },
        <ConfirmExternalEvidenceForm
          initialValues={{
            trainingModuleId: row.trainingModuleId,
            trainingModuleLabel: formatTrainingModuleLabel(row.trainingModuleCode, row.trainingModuleName),
            trainingPartId: row.trainingPartId,
            trainingPartLabel: formatTrainingPartLabel(row.trainingPartVersionCode, row.trainingMethod),
            issuedAt: row.issuedAt,
            expiresAt: row.expiresAt,
            notes: row.notes || '',
          }}
          onSubmitForm={payload => updateEvidence(evidenceId, payload)}
          rejectAction={handleCloseModal}
          submitting={updateState.loading}
        />,
      ),
    );
  };

  const handleDelete = event => {
    const evidenceId = Number(event.currentTarget.dataset.key);
    const [config, element] = getConfirmationModalConfig({
      title: t('trainingSessionDetails.deleteEvidenceTitle'),
      confirmAction: () => deleteEvidence(evidenceId),
      rejectAction: handleCloseModal,
      message: t('trainingSessionDetails.deleteEvidenceMessage'),
    });
    dispatch(modalActions.openModal(config, element));
  };

  const handleAddCertificate = evidenceId => {
    dispatch(
      modalActions.openModal(
        {
          size: 'medium',
          title: t('trainingDetails.addCertificateModalTitle'),
        },
        <SendFileForm
          fieldName="certificate"
          onSubmitForm={value => addFile(evidenceId, value)}
          rejectAction={handleCloseModal}
          submitting={addFileState.loading}
          fileFormat=".pdf"
        />,
      ),
    );
  };

  const handleShowCertificate = async (evidenceId, certificate) => {
    const previewUrl =
      typeof certificate === 'string'
        ? certificate
        : typeof certificate?.previewUrl === 'string'
        ? certificate.previewUrl
        : '';

    if (certificate && !previewUrl) {
      setDownloadingCertificateId(evidenceId);
      try {
        if (
          await handleCertificateDownload({
            certificate,
            courseId: evidenceId,
            userId,
            downloadCertificate: id => downloadFile(id),
            dispatch,
            t,
          })
        ) {
          tableData.handleForceUpdate();
          return;
        }
      } finally {
        setDownloadingCertificateId(null);
      }
    }

    const certificateUrl = previewUrl || null;
    const picture = certificateUrl ? (
      <img src={certificateUrl} width="200" height="300" alt="" />
    ) : (
      <Icon color="primary" style={{ fontSize: 150 }}>
        image
      </Icon>
    );

    const row = (Array.isArray(tableData.data) ? tableData.data : []).find(
      item => Number(item.id) === Number(evidenceId),
    );
    const canDeleteCertificate = row && isExternalSource(row.source);

    dispatch(
      modalActions.openModal(
        {
          size: 'large',
          title: t('trainingDetails.certificateModalTitle'),
          customStyles: classes.modal,
        },
        <div className={classes.wrapper}>
          {picture}
          <div className={classes.btnWrapper}>
            <Button
              className={classes.btn}
              variant="contained"
              color="primary"
              size="large"
              onClick={() => {
                downloadFile(evidenceId);
                handleCloseModal();
              }}
            >
              {t('buttons.save')}
            </Button>
            <Button
              className={`${classes.btn} ${classes.btnDelete}`}
              variant="outlined"
              size="large"
              onClick={handleCloseModal}
            >
              {t('buttons.cancel')}
            </Button>
            {canDeleteCertificate && (
              <Button
                className={classes.btn}
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => {
                  const [config, element] = getConfirmationModalConfig({
                    title: t('trainingSessions.deleteCertificateTitle'),
                    confirmAction: () => deleteFile(evidenceId),
                    rejectAction: handleCloseModal,
                    message: t('trainingSessions.deleteCertificateMessage'),
                  });
                  dispatch(modalActions.openModal(config, element));
                }}
              >
                {t('trainingSessions.deleteCertificate')}
              </Button>
            )}
          </div>
        </div>,
      ),
    );
  };

  const rows = (Array.isArray(tableData.data) ? tableData.data : []).map(item => {
    const isExternal = isExternalSource(item.source);
    const isElearningLazy = !isExternal && item.deliveryMethod === 'elearning';
    const certificate = item.certificatePreviewUrl
      ? { previewUrl: item.certificatePreviewUrl }
      : item.hasCertificate
      ? {}
      : null;

    return {
      ...item,
      sourceLabel: mapEvidenceSourceLabel(item.source),
      trainingModuleLabel: formatTrainingModuleLabel(item.trainingModuleCode, item.trainingModuleName),
      trainingPartLabel: formatTrainingPartLabel(item.trainingPartVersionCode, item.trainingMethod),
      evidenceParams: {
        rowId: item.id,
        hasCertificate: Boolean(item.hasCertificate),
        hasAssessmentResult: Boolean(item.hasAssessmentResult),
        isPerformedWithSuccess: true,
        certificate,
        isMoodleCourse: isElearningLazy,
        // Manual upload when no cert and not elearning lazy (external + classroom/VC session).
        allowUpload: !isElearningLazy,
      },
    };
  });

  const tableConfig = getEvidenceHistoryConfig({
    handleAddCertificate,
    handleShowCertificate,
    handleDownloadQuizAnswers: () => {},
    isDownloadingCertificate: id => String(downloadingCertificateId) === String(id),
  });
  const [columns, tableRows] = transformTableData(tableConfig, rows);

  const tableActions = [
    {
      type: 'icon',
      icon: 'edit',
      onClick: handleEdit,
      getHidden: rowData =>
        rowData.canEditExternalEvidence === undefined
          ? !isExternalSource(rowData.source)
          : !rowData.canEditExternalEvidence,
    },
    {
      type: 'icon',
      icon: 'delete',
      color: 'error',
      onClick: handleDelete,
      getHidden: rowData =>
        rowData.canDeleteExternalEvidence === undefined
          ? !isExternalSource(rowData.source)
          : !rowData.canDeleteExternalEvidence,
    },
  ];

  const isLoading =
    updateState.loading ||
    deleteState.loading ||
    addFileState.loading ||
    deleteFileState.loading ||
    tableData.isFetchingTableData;

  return (
    <ModalBusyContainer busy={isLoading}>
      <AppTable
        columns={columns}
        data={tableRows}
        totalCount={tableData.totalCount}
        rowsPerPage={tableData.pageSize}
        isLazyLoading
        onChangePage={tableData.handleChangePage}
        onColumnSorting={tableData.handleSorting}
        actions={tableActions}
      />
    </ModalBusyContainer>
  );
};

EvidenceHistoryTable.propTypes = {
  tableData: PropTypes.shape(withTableProps).isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  helpers: PropTypes.shape({
    updateExternalEvidence: PropTypes.func.isRequired,
    deleteExternalEvidence: PropTypes.func.isRequired,
    uploadEvidenceCertificate: PropTypes.func.isRequired,
    downloadEvidenceCertificate: PropTypes.func.isRequired,
    deleteEvidenceCertificate: PropTypes.func.isRequired,
  }).isRequired,
};

export default EvidenceHistoryTable;
